<template>
  <div class="flex justify-start">
    <div class="flex-1">
      <div v-if="isLoading" class="p-8 flex flex-col items-center justify-center">
        <div class="loading-spinner mb-4"></div>
        <p class="text-gray-600">{{ loadingMessage }}</p>
        <div class="w-64 h-2 bg-gray-200 rounded-full mt-4 overflow-hidden">
          <div class="h-full bg-orange-500 rounded-full loading-bar"></div>
        </div>
      </div>
      <div v-else-if="swaggerError" class="p-4 text-red-600">
        {{ swaggerError }}
        <button @click="retryLoad" class="ml-4 px-3 py-1 bg-orange-500 text-white rounded hover:bg-orange-600">
          Retry
        </button>
      </div>
      <div v-else class="swagger" id="swagger"></div>
    </div>
    <div class="flex-none mt-4 mr-7">
      <button
        ref="copybtn"
        class="btn"
        @click="handleCopyToClipboard"
        @mouseleave="removeFocus"
      >
        Copy AccessToken
      </button>
      <div>
        <p>Access token expires in:<br />{{ formattedRemainingTime }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import msalService from "@/_auth/msalService";
import { useStore } from "vuex";
import { computed, onMounted, ref, nextTick } from "vue";
import copyToClipboard from "@/_utils/CopyToClipboard";

export default {
  name: "SwaggerComponent",
  setup() {
    const store = useStore();
    const accessTokenCopied = ref(false);
    const isLoading = ref(true);
    const swaggerError = ref("");
    const loadingMessage = ref("Initializing...");
    const timeRemaining = computed(() => {
      return store.getters.getRemainingTimeToken;
    });
    const remainingTime = ref(0);

    const tokenExpiresOn = computed(() => {
      return store.getters.getTokenExp;
    });

    const hasValidToken = computed(() => {
      return store.getters.getIsTokenValid;
    });

    const hasApiDefinition = computed(() => {
      const apiDefinition = store.getters.getApiDefinition;
      return (
        apiDefinition &&
        typeof apiDefinition === "object" &&
        Object.keys(apiDefinition).length > 0
      );
    });

    const copybtn = ref(null);

    const removeFocus = () => {
      setTimeout(() => {
        copybtn.value.blur();
      }, 50);
    };

    const startCountDown = () => {
      console.log("starting countdown");

      remainingTime.value = timeRemaining.value;

      setInterval(() => {
        remainingTime.value--;

        if (remainingTime.value < 100) {
          console.log("renewing token");
          msalService.getToken();
        }
      }, 1000);
    };

    const formattedRemainingTime = computed(() => {
      const minutes = Math.floor(remainingTime.value / 60);
      const seconds = remainingTime.value % 60;
      return `${minutes} minutes ${seconds} seconds`;
    });

    const handleCopyToClipboard = () => {
      if (!hasValidToken.value) {
        msalService.getToken();
      }
      const accessToken = "Bearer " + store.getters.getAccessToken;
      copyToClipboard(accessToken);
      accessTokenCopied.value = true;
    };

    const loadSwaggerUI = () => {
      return new Promise((resolve, reject) => {
        if (window.SwaggerUIBundle) {
          resolve(window.SwaggerUIBundle);
          return;
        }

        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = "https://unpkg.com/swagger-ui-dist@5/swagger-ui.css";
        document.head.appendChild(link);

        const script = document.createElement("script");
        script.src = "https://unpkg.com/swagger-ui-dist@5/swagger-ui-bundle.js";
        script.onload = () => resolve(window.SwaggerUIBundle);
        script.onerror = () => reject(new Error("Failed to load SwaggerUI"));
        document.head.appendChild(script);
      });
    };

    const initSwagger = async () => {
      isLoading.value = true;
      swaggerError.value = "";
      loadingMessage.value = "Checking authentication...";

      try {
        if (!hasValidToken.value) {
          loadingMessage.value = "Acquiring token...";
          await msalService.getToken();
        }
        
        loadingMessage.value = "Loading SwaggerUI library...";
        let SwaggerUIBundle;
        try {
          SwaggerUIBundle = await loadSwaggerUI();
        } catch (cdnError) {
          swaggerError.value = `Failed to load SwaggerUI from CDN: ${cdnError.message}`;
          isLoading.value = false;
          return;
        }
        
        if (!SwaggerUIBundle) {
          swaggerError.value = "SwaggerUIBundle not available after loading";
          isLoading.value = false;
          return;
        }
        
        loadingMessage.value = "Loading API documentation...";
        
        // Now show the #swagger div and initialize
        isLoading.value = false;
        await nextTick();
        
        const accessToken = store.getters.getAccessToken;
        
        SwaggerUIBundle({
          dom_id: "#swagger",
          url: "/api/openapi.json",
          requestInterceptor: (req) => {
            if (accessToken) {
              req.headers.Authorization = `Bearer ${accessToken}`;
            }
            return req;
          },
          onComplete: () => {
            console.log("SwaggerUI loaded successfully");
          },
          onFailure: (err) => {
            console.error("SwaggerUI failed:", err);
            swaggerError.value = `SwaggerUI failed to render: ${err}`;
          }
        });
      } catch (error) {
        const errMsg = error?.message || "Failed to load OpenAPI definition";
        swaggerError.value = `Swagger/OpenAPI is not reachable: ${errMsg}`;
        isLoading.value = false;
      }
    };

    const retryLoad = () => {
      initSwagger();
    };

    onMounted(() => {
      startCountDown();
      initSwagger();
    });

    return {
      hasValidToken,
      hasApiDefinition,
      handleCopyToClipboard,
      accessTokenCopied,
      tokenExpiresOn,
      timeRemaining,
      formattedRemainingTime,
      copybtn,
      removeFocus,
      isLoading,
      swaggerError,
      loadingMessage,
      retryLoad
    };
  }
};
</script>

<style scoped>
.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #f97316;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-bar {
  animation: loading 2s ease-in-out infinite;
}

@keyframes loading {
  0% { width: 0%; }
  50% { width: 70%; }
  100% { width: 100%; }
}
</style>
